import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { logout, setTokens } from '../features/auth/authSlice';

// Derive the API base from the host the app is opened on, so it works whether you browse to
// http://localhost:3000 (→ localhost:5062) or http://<lan-ip>:3000 (→ <lan-ip>:5062).
// Override explicitly with REACT_APP_API_URL (e.g. in production / Firebase hosting).
// const apiHost = (typeof window !== 'undefined' && window.location && window.location.hostname) || 'https://healingapi.icygrass-68e73490.centralindia.azurecontainerapps.io';
const API_URL = process.env.REACT_APP_API_URL || `https://healingapi.icygrass-68e73490.centralindia.azurecontainerapps.io/api`;

const rawBaseQuery = fetchBaseQuery({
  baseUrl: API_URL,
  prepareHeaders: (headers, { getState }) => {
    const token = getState().auth?.accessToken;
    if (token) headers.set('Authorization', `Bearer ${token}`);
    headers.set('Accept', 'application/json');
    return headers;
  },
});

const baseQueryWithReauth = async (args, api, extraOptions) => {
  // Short-circuit: if the user has no token (post-logout, pre-login), don't fire
  // the request at all. RTK polling, refetchOnFocus and refetchOnReconnect would
  // otherwise spam the network log with 401s during the unmount window. The
  // synthetic 401 still flows through the SessionGuard's transition handler.
  const url = typeof args === 'string' ? args : args?.url;
  const isAuthCall = String(url ?? '').startsWith('/auth/');
  const haveToken = Boolean(api.getState()?.auth?.accessToken);
  if (!haveToken && !isAuthCall) {
    return { error: { status: 401, data: { error: { code: 'NoToken', message: 'Not authenticated' } } } };
  }

  let result = await rawBaseQuery(args, api, extraOptions);
  if (result?.error?.status === 401) {
    const { refreshToken, loginAt } = api.getState().auth ?? {};
    // Block refresh if the absolute 90-minute session ceiling has passed.
    const ABSOLUTE_MS = 90 * 60 * 1000;
    const pastAbsoluteCeiling = loginAt && (Date.now() - loginAt >= ABSOLUTE_MS);
    if (refreshToken && !pastAbsoluteCeiling) {
      const refreshResult = await rawBaseQuery(
        { url: '/auth/refresh', method: 'POST', body: { refreshToken } },
        api,
        extraOptions
      );
      if (refreshResult?.data?.success) {
        const data = refreshResult.data.data;
        api.dispatch(setTokens({
          accessToken: data.accessToken,
          refreshToken: data.refreshToken,
          accessTokenExpiresAt: data.accessTokenExpiresAt,
          refreshTokenExpiresAt: data.refreshTokenExpiresAt,
          user: data.user,
        }));
        result = await rawBaseQuery(args, api, extraOptions);
      } else {
        api.dispatch(logout());
      }
    } else {
      api.dispatch(logout());
    }
  }
  return result;
};

// Exported so the topbar's manual Refresh button can invalidate every tag at once — a full
// data refresh without reloading the page or flashing every screen to a blank loading state
// the way `baseApi.util.resetApiState()` would (that wipes the cache first; invalidating just
// marks it stale and refetches in the background, swapping in fresh data when it arrives).
export const TAG_TYPES = [
  'Auth', 'User', 'Lead', 'LeadActivity', 'LeadFollowUp', 'Patient',
  'Consultation', 'TreatmentPlan', 'TreatmentType', 'Session', 'SessionPackage', 'Appointment',
  'Invoice', 'Payment', 'Template', 'Message', 'Dashboard', 'CallLog',
  'ClinicSettings', 'TaskRule', 'ClinicTask', 'TaskColumn', 'RuleKind', 'WhatsAppStatus', 'WaTemplate',
  'WaConversation', 'WaMessage', 'WaMedia', 'Audience', 'Automation', 'AutomationRun',
  // 'ProcedureCatalog' was previously used by procedureCatalogApi without being declared here,
  // which silently made its cache invalidation unreliable.
  'ProcedureCatalog', 'SessionLedger',
];

export const baseApi = createApi({
  reducerPath: 'api',
  baseQuery: baseQueryWithReauth,
  // Re-pull whenever the tab regains focus or the network comes back so users
  // don't sit on stale data after switching tabs / suspending the laptop.
  refetchOnFocus: true,
  refetchOnReconnect: true,
  tagTypes: TAG_TYPES,
  endpoints: () => ({}),
});

export const unwrap = (resp) => resp?.data;
